import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode } from 'react';
import { Pressable, StyleProp, TextStyle, ViewStyle } from 'react-native';
import MyAppText from './MyAppText';
import { Hoverable } from 'react-native-web-hover';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

type MyCheckboxParameters = {
    label: string | ReactNode;
    style?: StyleProp<ViewStyle> | undefined;
    textStyle?: StyleProp<TextStyle> | undefined;
    textContainerStyle?: StyleProp<TextStyle> | undefined;
    checkBoxStyle?: StyleProp<TextStyle> | undefined;
    disabled?: boolean;
    customIcon?: ReactNode;
    labelStartContent?: ReactNode;
} & ({
    checked: boolean;
    setChecked: (value: boolean) => void;
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
} | {
    checked?: never;
    setChecked?: never;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
});

export function MyCheckbox({
    label,
    checked,
    setChecked,
    style,
    textStyle,
    disabled,
    checkBoxStyle,
    customIcon,
    textContainerStyle,
    labelStartContent,
    form,
    fieldName,
    customValidations,
    pattern
}: MyCheckboxParameters) {
    const { styles, theme } = useStyles(styleSheet);

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (pattern) {
            rules.pattern = pattern;
        }

        if (customValidations) {
            rules.validate = {
                ...customValidations
            };
        }
        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field }) => (
                <Hoverable style={[styles.checkboxContainer, style]}>
                    {({ hovered }) => (<>
                        <Pressable
                            style={[styles.checkboxBase(hovered, field.value, disabled ?? false), checkBoxStyle]}
                            onPress={() => field.onChange(!field.value)}
                            disabled={disabled}>
                            {field.value ?
                                customIcon
                                    ? customIcon
                                    : <FontAwesomeIcon icon={faCheck} fontSize={14} color={theme.colors.checkbox.checked.icon} /> : null}
                        </Pressable>
                        <Pressable
                            onPress={() => field.onChange(!field.value)}
                            disabled={disabled}
                            style={textContainerStyle ?? { flex: 1 }}
                        >
                            {labelStartContent}
                            {typeof label === 'string'
                                ? <MyAppText style={[styles.checkboxLabel, textStyle]}>{label}</MyAppText>
                                : label
                            }
                        </Pressable>
                    </>)}
                </Hoverable>
            )}
        />;
    } else if (checked !== undefined && setChecked !== undefined) {
        return (
            <Hoverable style={[styles.checkboxContainer, style]}>
                {({ hovered }) => (<>
                    <Pressable
                        style={[styles.checkboxBase(hovered, checked, disabled ?? false), checkBoxStyle]}
                        onPress={() => setChecked(!checked)}
                        disabled={disabled}>
                        {checked ?
                            customIcon
                                ? customIcon
                                : <FontAwesomeIcon icon={faCheck} fontSize={14} color={theme.colors.checkbox.checked.icon} /> : null}
                    </Pressable>
                    <Pressable
                        onPress={() => setChecked(!checked)}
                        disabled={disabled}
                        style={textContainerStyle ?? { flex: 1 }}
                    >
                        {labelStartContent}
                        {typeof label === 'string'
                            ? <MyAppText style={[styles.checkboxLabel, textStyle]}>{label}</MyAppText>
                            : label
                        }
                    </Pressable>
                </>)}
            </Hoverable>
        );
    }
}

const styleSheet = createStyleSheet((theme) => ({
    checkboxContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 8,
        flex: 1,
    },
    checkboxBase: (hovered: boolean, checked: boolean, disabled: boolean) => ({
        width: 20,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: checked ? (disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.checkbox.checked.hoverBorder : theme.colors.checkbox.checked.border) : disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.checkbox.unchecked.hoverBorder : theme.colors.checkbox.unchecked.border,
        backgroundColor: checked ? (disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.checkbox.checked.hoverBackground : theme.colors.checkbox.checked.background) : disabled ? undefined : theme.colors.checkbox.unchecked.background,
    }),
    checkboxLabel: {
        color: theme.colors.textColor,
        fontSize: 13,
        flex: 1,
    }
}));
